type InsuranceWithClinicScope = {
  agency_clinic_id?: string | null;
};

/**
 * Returns agency insurances valid for a selected clinic.
 * - If no clinic is selected, return all records.
 * - Include clinic-scoped records for the selected clinic.
 * - Optionally include agency-wide records with no clinic scope.
 */
export function filterAgencyInsurancesByClinic<
  T extends InsuranceWithClinicScope,
>(
  agencyInsurances: T[],
  agencyClinicId: string | null | undefined,
  includeAgencyWide = true
): T[] {
  if (!agencyClinicId) {
    return agencyInsurances;
  }

  return agencyInsurances.filter(
    insurance =>
      insurance.agency_clinic_id === agencyClinicId ||
      (includeAgencyWide && !insurance.agency_clinic_id)
  );
}

/** Shown beside a payer that is active in Roger but that WellSky has retired. */
export const RETIRED_PAYER_LABEL = 'Retired';

// Eligibility indicator prefix (check mark + two non-breaking spaces)
export const ELIGIBILITY_INDICATOR = '✓\u00A0\u00A0';

type IntakeInsurance = {
  id: string;
  name: string;
  edi_payer_id?: string | null;
  agency_clinic_id?: string | null;
  eligibility_supported?: boolean;
  retired_in_wellsky?: boolean;
};

/**
 * The intake's payer select: one option per name and payer ID, retired payers
 * last and labelled, then ✓ (eligibility), then alphanumeric names. A duplicate
 * keeps the intake's own payer, else an unretired one, whatever the row order.
 * An own payer in no list (inactive in Roger) is added last, unlabelled.
 */
export function intakeAgencyInsuranceOptions(
  agencyInsurances: IntakeInsurance[],
  agencyClinicId: string | null | undefined,
  intakeInsurance?: IntakeInsurance | null
): { value: string; label: string }[] {
  const rank = (insurance: IntakeInsurance) =>
    insurance.id === intakeInsurance?.id
      ? 0
      : insurance.retired_in_wellsky
        ? 2
        : 1;
  const kept = new Map<string, IntakeInsurance>();
  for (const insurance of filterAgencyInsurancesByClinic(
    agencyInsurances,
    agencyClinicId
  )) {
    const key = `${insurance.name}_${insurance.edi_payer_id}`;
    const current = kept.get(key);
    if (!current || rank(insurance) < rank(current)) {
      kept.set(key, insurance);
    }
  }

  const name = (insurance: IntakeInsurance) =>
    `${insurance.name}${insurance.edi_payer_id ? ` (${insurance.edi_payer_id})` : ''}`;
  const ticked = (insurance: IntakeInsurance) =>
    `${insurance.eligibility_supported ? ELIGIBILITY_INDICATOR : ''}${name(insurance)}`;
  const options = Array.from(kept.values())
    .sort((a, b) => {
      const retired =
        Number(Boolean(a.retired_in_wellsky)) -
        Number(Boolean(b.retired_in_wellsky));
      if (retired) return retired;
      // The same answer the run button is gated on. Marking a payer here that
      // the button then refuses is the confusion this indicator exists to save
      // the user from.
      const eligibility =
        Number(Boolean(b.eligibility_supported)) -
        Number(Boolean(a.eligibility_supported));
      if (eligibility) return eligibility;
      const aAlphanum = /^[a-zA-Z0-9]/.test(name(a));
      const bAlphanum = /^[a-zA-Z0-9]/.test(name(b));
      if (aAlphanum !== bAlphanum) return aAlphanum ? -1 : 1;
      return name(a).localeCompare(name(b));
    })
    .map(insurance => ({
      value: insurance.id,
      label: `${ticked(insurance)}${insurance.retired_in_wellsky ? ` · ${RETIRED_PAYER_LABEL}` : ''}`,
    }));
  if (
    intakeInsurance &&
    !agencyInsurances.some(insurance => insurance.id === intakeInsurance.id)
  ) {
    options.push({ value: intakeInsurance.id, label: ticked(intakeInsurance) });
  }
  return options;
}
